/**
 * The Careers area's URL shapes, in one place — the route table, the header tab, the root redirect, the
 * department switcher and the pages all build these paths, and spelling them out at each call site is how
 * the Staffing area's switcher came to disagree with its routes (see `staffingRoutes.ts`).
 *
 * Careers lives under `/:departmentId` like every other department tab: `/{department}/careers`. The
 * department in the URL is the one whose jobs and candidates the page shows.
 */

/** The Careers area's path segment, right after the department — also the page's pattern in the route table. */
export const CAREERS_SEGMENT = "careers";

/** The Careers page (Pipeline + Jobs tabs) in one department. */
export const careersPath = (departmentId: string): string => `/${departmentId}/${CAREERS_SEGMENT}`;

/** `careers?tab=` — which tab (`pipeline` | `jobs`) the page shows. */
export const CAREERS_TAB_PARAM = "tab";
/** `careers?job=` — the Pipeline's job: a job id, or "all". */
export const CAREERS_JOB_PARAM = "job";

/** The Pipeline tab on one job — its board, the tab's everyday view. */
export const careersPipelinePath = (departmentId: string, jobId: string): string =>
	`${careersPath(departmentId)}?${new URLSearchParams({ [CAREERS_TAB_PARAM]: "pipeline", [CAREERS_JOB_PARAM]: jobId }).toString()}`;

/**
 * Static segment under `/careers` that selects one APPLICATION's profile — a person on one job. The same
 * person on two jobs has two profiles (each with its own step, notes and status), linked by "Also applied to".
 */
const APPLICATIONS_SEGMENT = "applications";

/** The profile route's pattern, for the route table (relative to `/:departmentId`). */
export const CAREERS_APPLICATION_ROUTE = `${CAREERS_SEGMENT}/${APPLICATIONS_SEGMENT}/:applicationId`;

/** One application's profile page. */
export const careersApplicationPath = (departmentId: string, applicationId: string): string =>
	`${careersPath(departmentId)}/${APPLICATIONS_SEGMENT}/${applicationId}`;

/**
 * `/{department}/careers/applications/{id}?note=` — the profile with its "All notes" panel open on one note, highlighted. A bell
 * @mention lands here. Closing the panel drops the param, so Back and a reload don't reopen it.
 */
export const CAREERS_NOTE_PARAM = "note";

/** One application's profile, opened on one of its notes. */
export const careersApplicationNotePath = (departmentId: string, applicationId: string, noteId: string): string =>
	`${careersApplicationPath(departmentId, applicationId)}?${new URLSearchParams({ [CAREERS_NOTE_PARAM]: noteId }).toString()}`;

/** `?feedback=` — the same as `?note=`, for a feedback: the profile on its Feedback tab, "All feedback" open on it. */
export const CAREERS_FEEDBACK_PARAM = "feedback";

/** One application's profile, opened on one of its feedback. */
export const careersApplicationFeedbackPath = (departmentId: string, applicationId: string, feedbackId: string): string =>
	`${careersApplicationPath(departmentId, applicationId)}?${new URLSearchParams({ [CAREERS_FEEDBACK_PARAM]: feedbackId }).toString()}`;

/**
 * Router `state` a Careers list (board card, table row) sets when it opens a profile. It is the profile's
 * only proof that the entry behind it IS the pipeline: the browser's back stack also holds whatever page
 * came before a profile link that was pasted or bookmarked.
 */
export const CAREERS_PROFILE_FROM_LIST_STATE = { fromCareersList: true } as const;

/** Whether the profile was opened from a Careers list, so Back can return to it with its tab and job intact. */
export const isFromCareersList = (state: unknown): boolean =>
	typeof state === "object" && state !== null && "fromCareersList" in state && state.fromCareersList === true;

/** True for `/{department}/careers` and anything under it — by segment, so `/{department}/careers-foo` is not. */
export const isCareersPath = (pathname: string): boolean => pathname.split("/")[2] === CAREERS_SEGMENT;
